/**
 * Feed Module for TitCock.tv
 *
 * Loads the direct RedGifs metadata generated by direct_scraper.py.
 */

import { CONFIG, feedState } from "./state.js";

let cachedVideos = [];

const shuffle = (items) => {
  for (let i = items.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [items[i], items[j]] = [items[j], items[i]];
  }
};

const normalizeVideo = (video, cache) => {
  if (!video?.slug || !video?.name || !video?.videoSrc) return null;

  return {
    slug: String(video.slug),
    name: String(video.name),
    category: cache.category,
    categoryLabel: cache.label,
    redgifsUrl: video.redgifsUrl || `https://www.redgifs.com/watch/${video.slug}`,
    title: video.title || "",
    author: video.author || "",
    likes: video.likes == null ? null : Number(video.likes) || 0,
    thumbnail: video.thumbnail || "",
    videoSrc: String(video.videoSrc),
  };
};

export const buildVideoUrl = (name) => {
  return `https://media.redgifs.com/${name}.mp4`;
};

export const initFeed = async () => {
  try {
    const results = await Promise.allSettled(
      CONFIG.DIRECT_VIDEO_CACHES.map(async (cache) => {
        const response = await fetch(cache.path, {
          credentials: "omit",
          cache: "reload",
          referrerPolicy: "no-referrer",
        });

        if (!response.ok) {
          throw new Error(`${cache.path} returned ${response.status}`);
        }

        const json = await response.json();
        return Array.isArray(json?.videos) ? json.videos : [];
      }),
    );

    const videosBySlug = new Map();
    results.forEach((result, index) => {
      if (result.status === "rejected") {
        console.warn(
          `[Feed] Failed to load ${CONFIG.DIRECT_VIDEO_CACHES[index].path}:`,
          result.reason,
        );
        return;
      }

      result.value
        .map((video) => normalizeVideo(video, CONFIG.DIRECT_VIDEO_CACHES[index]))
        .filter(Boolean)
        .forEach((video) => {
          if (!videosBySlug.has(video.slug)) videosBySlug.set(video.slug, video);
        });
    });

    cachedVideos = [...videosBySlug.values()];
    if (!cachedVideos.length) {
      throw new Error("No direct videos were loaded from any niche cache");
    }
    shuffle(cachedVideos);

    feedState.cacheIndex = 0;
    feedState.seenSlugs.clear();
    feedState.initialFeedLoaded = false;

    console.log(`[Feed] Loaded ${cachedVideos.length} direct videos`);
  } catch (err) {
    console.error("Failed to load local direct-video cache:", err);
    cachedVideos = [];
  }
};

export const fetchFeed = async () => {
  if (feedState.isLoading) return [];

  feedState.isLoading = true;

  try {
    if (!cachedVideos.length || !feedState.selectedCategories.size) return [];

    const pageSize = feedState.initialFeedLoaded
      ? CONFIG.SUBSEQUENT_POST_LIMIT
      : CONFIG.INITIAL_POST_LIMIT;
    const videos = [];
    let scanned = 0;

    while (videos.length < pageSize && scanned < cachedVideos.length) {
      if (feedState.cacheIndex >= cachedVideos.length) {
        feedState.cacheIndex = 0;
        feedState.seenSlugs.clear();
        shuffle(cachedVideos);
      }

      const video = cachedVideos[feedState.cacheIndex];
      feedState.cacheIndex += 1;
      scanned += 1;

      if (!feedState.selectedCategories.has(video.category)) continue;
      if (feedState.seenSlugs.has(video.slug)) continue;
      feedState.seenSlugs.add(video.slug);
      videos.push(video);
    }

    feedState.initialFeedLoaded = true;
    return videos;
  } finally {
    feedState.isLoading = false;
  }
};

export const fetchPost = async (slug) => {
  const normalizedSlug = String(slug || "").trim().toLowerCase();
  return cachedVideos.find((video) => video.slug.toLowerCase() === normalizedSlug) || null;
};
